import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, NewsletterMeta } from "../api";
import { useAuth } from "../auth";

function fmt(ts: number) {
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function NewslettersList() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NewsletterMeta[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.listNewsletters().then(setItems).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    const title = prompt("Newsletter title?", "Vox Imperialis — New Issue");
    if (title === null) return;
    try {
      const n = await api.createNewsletter(title || "Untitled issue");
      navigate(`/newsletters/${n.id}`);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  const togglePublish = async (n: NewsletterMeta) => {
    const publish = n.status !== "published";
    if (publish && !confirm(`Publish "${n.title}"? It will become visible to all viewers.`)) return;
    await api.publishNewsletter(n.id, publish);
    load();
  };

  const remove = async (n: NewsletterMeta) => {
    if (!confirm(`Delete "${n.title}"? This cannot be undone.`)) return;
    await api.deleteNewsletter(n.id);
    load();
  };

  return (
    <div className="page-wrap">
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 30, margin: 0 }}>Newsletters</h1>
        <button className="btn btn-accent" style={{ marginLeft: "auto" }} onClick={create}>
          + New issue
        </button>
      </div>

      {err && <p style={{ color: "var(--accent)" }}>{err}</p>}
      {!items && !err && <p className="mono" style={{ color: "var(--muted)", fontSize: 12 }}>Loading…</p>}
      {items && items.length === 0 && <p style={{ color: "var(--muted)" }}>No newsletters yet.</p>}

      {items && items.length > 0 && (
        <div style={{ border: "1px solid var(--rule2)" }}>
          {items.map((n, i) => (
            <div
              key={n.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 120px auto",
                alignItems: "center",
                gap: 12,
                padding: "14px 18px",
                borderTop: i ? "1px solid var(--rule2)" : "none",
                background: i % 2 ? "var(--paper2)" : "var(--paper)",
              }}
            >
              <div>
                <Link to={`/newsletters/${n.id}`} style={{ fontFamily: "'Zilla Slab',sans-serif", fontWeight: 600, fontSize: 18, color: "var(--ink)" }}>
                  {n.title}
                </Link>
                <div className="mono" style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
                  updated {fmt(n.updatedAt)}
                  {n.status === "published" && n.publishedAt ? ` · published ${fmt(n.publishedAt)}` : ""}
                </div>
              </div>
              <span
                className="mono"
                style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em", color: n.status === "published" ? "var(--gold)" : "var(--muted)" }}
              >
                {n.status}
              </span>
              <div>
                {n.status === "published" && (
                  <Link to={`/read/${n.slug}`} className="miniadd">
                    read →
                  </Link>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                {can("admin") && (
                  <button className="miniadd" onClick={() => togglePublish(n)}>
                    {n.status === "published" ? "unpublish" : "publish"}
                  </button>
                )}
                <button className="miniadd" onClick={() => remove(n)}>
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
